// src/components/admin/ViewArtistMerchModal.js
import React, { useEffect, useState } from "react";
import {
    X,
    ShoppingBag,
    BadgeCheck,
    Clock,
    Info,
    Loader2,
    Shirt
} from "lucide-react";
import { useArtistMerch } from "../../services/admin/ArtistMerchServices";

export default function ViewArtistMerchModal({ itemId, isOpen, onClose }) {
    const { fetchMerchProductById, loading } = useArtistMerch();
    const [item, setItem] = useState(null);

    useEffect(() => {
        if (isOpen && itemId) {
            const loadItem = async () => {
                const data = await fetchMerchProductById(itemId);
                setItem(data);
            };
            loadItem();
        } else if (!isOpen) {
            setItem(null);
        }
    }, [isOpen, itemId, fetchMerchProductById]);

    if (!isOpen) return null;

    const getStatusStyle = (status) => {
        return status === 'active'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
            : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400';
    };

    const getStockStyle = (stock) => {
        if (stock === 0) return 'text-red-500 font-bold';
        if (stock < 10) return 'text-amber-500 font-bold';
        return 'text-green-500 font-bold';
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-hidden">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} />
            <div className="relative bg-white dark:bg-[#1A1A1A] w-full max-w-3xl max-h-[90vh] rounded-3xl shadow-2xl overflow-y-auto transform transition-all border border-black/5 dark:border-white/5">
                <div className="sticky top-0 z-10 bg-white/80 dark:bg-[#1A1A1A]/80 backdrop-blur-md px-6 py-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
                    <h2 className="text-xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                        Merchandise Details
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-full text-black hover:text-black/70 dark:text-white/60 dark:hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {loading || !item ? (
                    <div className="p-20 flex flex-col items-center justify-center gap-4">
                        <Loader2 className="animate-spin text-jazz-brown" size={48} />
                        <p className="text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Fetching details...</p>
                    </div>
                ) : (
                    <div className="p-6 sm:p-8 animate-in fade-in duration-500">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                            <div className="md:col-span-5 space-y-6">
                                <div className="aspect-square w-full rounded-2xl bg-[#F5F5F5] dark:bg-zinc-800 border border-black/5 overflow-hidden shadow-lg shadow-black/5">
                                    {item.image ? (
                                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Shirt size={48} className="text-black/10" />
                                        </div>
                                    )}
                                </div>

                                <div className="bg-jazz-brown/5 p-4 rounded-xl border border-jazz-brown/10">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Inventory Status</span>
                                        <ShoppingBag size={14} className="text-jazz-brown" />
                                    </div>
                                    <div className="space-y-2 text-xs">
                                        <div className="flex justify-between">
                                            <span className="text-black dark:text-white/50 uppercase tracking-wider font-bold">Price</span>
                                            <span className="text-black dark:text-white font-bold">${parseFloat(item.price).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-black dark:text-white/50 uppercase tracking-wider font-bold">Stock Qty</span>
                                            <span className={getStockStyle(item.stock_quantity)}>{item.stock_quantity === 0 ? "OUT OF STOCK" : item.stock_quantity}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-black dark:text-white/50 uppercase tracking-wider font-bold">Category</span>
                                            <span className="text-jazz-brown font-bold uppercase">{item.category_name}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="md:col-span-7 space-y-8">
                                <div className="space-y-4">
                                    <div className="flex flex-wrap items-center gap-3">
                                        {item.is_popular && (
                                            <span className="px-2 py-1 bg-jazz-brown text-white rounded text-xs font-bold uppercase tracking-[0.2em] flex items-center gap-1">
                                                <BadgeCheck size={10} /> Popular
                                            </span>
                                        )}
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusStyle(item.status)}`}>
                                            {item.status === 'active' ? <Clock size={12} /> : <X size={12} />}
                                            {item.status}
                                        </span>
                                    </div>

                                    <h1 className="text-4xl font-bold text-black dark:text-white leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                                        {item.name}
                                    </h1>
                                </div>

                                <div className="pt-6 border-t border-black/5 dark:border-white/5">
                                    <h3 className="text-xs font-bold text-black/60 uppercase tracking-[0.2em] mb-3">Product Description</h3>
                                    <div className="text-black dark:text-white/70 text-base leading-relaxed whitespace-pre-wrap">
                                        {item.description}
                                    </div>
                                </div>

                                <div className="pt-6 border-t border-black/5 dark:border-white/5 bg-gray-50 dark:bg-white/5 p-4 rounded-2xl">
                                    <div className="flex items-center gap-3">
                                        <Info size={18} className="text-jazz-brown" />
                                        <p className="text-xs text-black/70 dark:text-white/50 font-bold uppercase tracking-widest leading-relaxed">
                                            This is a physical merchandise product. Stock is decremented automatically when orders are placed.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="sticky bottom-0 bg-white dark:bg-[#1A1A1A] p-6 border-t border-black/5 dark:border-white/5">
                    <button onClick={onClose} className="w-full py-4 bg-black text-white rounded-xl text-xs font-bold tracking-widest hover:bg-jazz-brown transition-all uppercase shadow-lg">
                        Close Details
                    </button>
                </div>
            </div>
        </div>
    );
}
